import { CreatePrinterStationPayload, PrinterStation, UpdatePrinterStationPayload } from '@models/payload/printer';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import {
  deletePrinterStation,
  getPrinterStationCreate,
  getPrinterStationDetail,
  getPrinterStationUpdate,
  getPrinterStations,
  getVendorPrinterStationsByVendorId,
} from '@services/printers';
import { getPrinterStationData, savePrinterStationData } from '@utils/indexedDB/printerStationStorage';
// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';

export type PrinterStationType = PrinterStation;

interface PrinterStationsStateInterface {
  loading: boolean;
  printerStations: PrinterStationType[];
  detail: PrinterStationType | undefined;
}

const initialState: PrinterStationsStateInterface = {
  loading: false,
  printerStations: [],
  detail: undefined,
};

interface GetPrinterStationInput {
  vendorId?: string;
  id: string;
}

interface GetVendorPrinterStationByIdResponse {
  data: {
    printerStation: PrinterStationType | undefined;
  };
}

const getPrinterStationById = createAppAsyncThunk<GetVendorPrinterStationByIdResponse, GetPrinterStationInput>(
  'printerStations/getVendorPrinterStationById',
  async ({ id }) => {
    const res = await getPrinterStationDetail(id);
    return { data: { printerStation: res.data } };
  }
);

interface GetVendorPrinterStationsResponse {
  data: {
    printerStations: PrinterStationType[];
  };
}
// TODO: refactor to use react query
const getVendorPrinterStations = createAppAsyncThunk<GetVendorPrinterStationsResponse, boolean>(
  'printerStations/getVendorPrinterStations',
  async (networkStatus) => {
    let printerStations: PrinterStationType[];
    const printerStationVendorId = localStorage.getItem('defaultPrinterStationVendorId');
    if (networkStatus) {
      if (printerStationVendorId) {
        const res = await getVendorPrinterStationsByVendorId(printerStationVendorId);
        savePrinterStationData((res.data as PrinterStationType[]) || []);
        printerStations = res.data as PrinterStationType[];
      } else {
        const res = await getPrinterStations();
        savePrinterStationData(res.data || []);
        printerStations = res.data || [];
      }
    } else {
      printerStations = await getPrinterStationData();
    }
    return { data: { printerStations } };
  }
);

const createPrinterStation = createAppAsyncThunk<void, CreatePrinterStationPayload & { networkStatus: boolean }>(
  'printerStations/createVendorPrinterStations',
  async (payload, { dispatch }) => {
    await getPrinterStationCreate({ ...payload });
    await dispatch(getVendorPrinterStations(payload.networkStatus));
  }
);

interface UpdatePrinterStationInput extends UpdatePrinterStationPayload {}

const updatePrinterStationById = createAppAsyncThunk<
  GetVendorPrinterStationByIdResponse,
  UpdatePrinterStationInput & { networkStatus: boolean }
>('printerStations/updateVendorPrinterStations', async (input, { dispatch }) => {
  const res = await getPrinterStationUpdate(input);
  await dispatch(getVendorPrinterStations(input?.networkStatus));
  return { data: { printerStation: res.data } };
});

interface DeletePrinterStationInput {
  vendorId?: string;
  id: string;
}

const deletePrinterStationById = createAppAsyncThunk<void, DeletePrinterStationInput & { networkStatus: boolean }>(
  'printerStations/deleteVendorPrinterStations',
  async ({ id, networkStatus }, { dispatch }) => {
    await deletePrinterStation(id);
    await dispatch(getVendorPrinterStations(networkStatus));
  }
);

const printerStationsSlice = createSlice({
  name: 'printerStations',
  initialState,
  reducers: {
    clearDetail: (state) => ({
      ...state,
      detail: undefined,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<PrinterStationsStateInterface>) => {
    builder.addCase(getVendorPrinterStations.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getVendorPrinterStations.fulfilled, (state, action) => {
      const { printerStations } = action.payload.data;
      return {
        ...state,
        loading: false,
        printerStations,
      };
    });

    builder.addCase(createPrinterStation.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getPrinterStationById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getPrinterStationById.fulfilled, (state, action) => {
      const { printerStation } = action.payload.data;

      return {
        ...state,
        loading: false,
        detail: printerStation,
      };
    });

    builder.addCase(updatePrinterStationById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(updatePrinterStationById.fulfilled, (state, action) => {
      const { printerStation } = action.payload.data;

      return {
        ...state,
        loading: false,
        detail: printerStation,
      };
    });

    builder.addCase(deletePrinterStationById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(deletePrinterStationById.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default printerStationsSlice;
export {
  createPrinterStation,
  deletePrinterStationById,
  getPrinterStationById,
  getVendorPrinterStations,
  updatePrinterStationById,
};
